import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import RecurringSection from "@/components/RecurringSection";
import {
  Send, Plus, Play, Pause, X, Eye, Smartphone, Clock, Users, Trash2, CheckCircle2,
} from "lucide-react";

const VARS = ["{nome}", "{whatsapp}", "{email}"];

export default function Campaigns() {
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [monitorId, setMonitorId] = useState(null);

  const load = useCallback(() => api.get("/campaigns").then((r) => setCampaigns(r.data)), []);
  useEffect(() => {
    load();
    api.get("/tags").then((r) => setTags(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/whatsapp/templates").then((r) => setMetaTemplates((r.data.templates || []).filter((t) => t.status === "APPROVED" && t.name !== "hello_world"))).catch(() => {});
    if (location.state?.clientIds) setWizardOpen(true);
  }, [load, location.state]);

  useEffect(() => {
    const anyRunning = campaigns.some((c) => ["andamento", "paused"].includes(c.status));
    if (!anyRunning && !monitorId) return;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [campaigns, monitorId, load]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Disparos</h1><p className="text-sm text-slate-500 mt-1">Crie campanhas e envie mensagens em massa no WhatsApp.</p></div>
        <Button onClick={() => setWizardOpen(true)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="campaign-new-button"><Plus className="h-4 w-4 mr-2" /> Novo Disparo</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {campaigns.length === 0 && <p className="text-sm text-slate-400 col-span-2 text-center py-12">Nenhuma campanha ainda. Crie seu primeiro disparo.</p>}
        {campaigns.map((c) => <CampaignCard key={c.id} c={c} onOpen={() => setMonitorId(c.id)} onChanged={load} />)}
      </div>

      <RecurringSection tags={tags} templates={templates} />

      {wizardOpen && (
        <CampaignWizard
          tags={tags} templates={templates} metaTemplates={metaTemplates} presetClientIds={location.state?.clientIds}
          onClose={() => setWizardOpen(false)}
          onCreated={(id) => { setWizardOpen(false); load(); setMonitorId(id); }}
        />
      )}
      {monitorId && <MonitorDialog id={monitorId} onClose={() => { setMonitorId(null); load(); }} />}
    </div>
  );
}

function CampaignCard({ c, onOpen, onChanged }) {
  const pct = c.total ? Math.round(((c.sent + c.errors) / c.total) * 100) : 0;
  const statusMap = {
    andamento: { label: "Enviando", cls: "bg-emerald-100 text-emerald-700" },
    aguardando: { label: "Pausa entre envios", cls: "bg-sky-100 text-sky-700" },
    paused: { label: "Pausada", cls: "bg-amber-100 text-amber-700" },
    concluida: { label: "Concluída", cls: "bg-slate-100 text-slate-600" },
    cancelled: { label: "Cancelada", cls: "bg-red-100 text-red-700" },
    agendada: { label: "Agendada", cls: "bg-indigo-100 text-indigo-700" },
  };
  const st = statusMap[c.status] || statusMap.concluida;
  const del = async (e) => { e.stopPropagation(); await api.delete(`/campaigns/${c.id}`); toast.success("Campanha removida."); onChanged(); };
  const start = async (e) => { e.stopPropagation(); await api.post(`/campaigns/${c.id}/control`, { action: "start" }); toast.success("Campanha iniciada!"); onChanged(); };

  return (
    <div onClick={onOpen} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-emerald-500/30 transition-all cursor-pointer" data-testid={`campaign-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center"><Send className="h-4 w-4 text-emerald-600" /></div>
          <div><h3 className="font-semibold text-slate-800">{c.name}</h3><p className="text-xs text-slate-400">{fmtDateTime(c.created_at)}</p></div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
      </div>
      {c.scheduled_at && c.status === "agendada" && <p className="text-xs text-indigo-600 mt-3 flex items-center gap-1"><Clock className="h-3 w-3" /> Agendada para {fmtDateTime(c.scheduled_at)}</p>}
      <div className="mt-4">
        <Progress value={pct} className="h-2" />
        <div className="flex justify-between text-xs text-slate-500 mt-2">
          <span>{c.sent} enviadas</span><span>{c.pending} pendentes</span><span className="text-red-500">{c.errors} erros</span>
        </div>
      </div>
      <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100">
        {c.status === "agendada" && <Button size="sm" variant="ghost" onClick={start} className="text-emerald-600"><Play className="h-4 w-4 mr-1" /> Iniciar agora</Button>}
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpen(); }} className="text-slate-600"><Eye className="h-4 w-4 mr-1" /> Acompanhar</Button>
        <Button size="sm" variant="ghost" onClick={del} className="text-red-500 ml-auto"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function CampaignWizard({ tags, templates, metaTemplates = [], presetClientIds, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", message: "",
    audience_type: presetClientIds ? "selected" : "all",
    audience_value: null, client_ids: presetClientIds || [],
    interval_min: 1, interval_max: 3, max_messages: null, scheduled_at: null,
    selected_template_ids: [],
    template_name: null, template_language: "pt_BR",
  });
  const [preview, setPreview] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectMetaTpl = (name) => {
    if (!name) { setForm((f) => ({ ...f, template_name: null })); return; }
    const t = metaTemplates.find((x) => x.name === name);
    const body = t && (t.components || []).find((c) => c.type === "BODY");
    setForm((f) => ({ ...f, template_name: name, template_language: (t && t.language) || "pt_BR", message: body ? body.text : f.message }));
  };
  const toggleTpl = (id) => setForm((f) => ({
    ...f,
    selected_template_ids: f.selected_template_ids.includes(id)
      ? f.selected_template_ids.filter((x) => x !== id)
      : [...f.selected_template_ids, id],
  }));

  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  useEffect(() => {
    if (form.audience_type !== "selected") return;
    const t = setTimeout(() => {
      const url = `/clients?filter=com_whatsapp${clientQuery ? `&search=${encodeURIComponent(clientQuery)}` : ""}`;
      api.get(url).then((r) => setClientResults(r.data.slice(0, 50))).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [clientQuery, form.audience_type]);
  const toggleClient = (id) => setForm((f) => ({
    ...f,
    client_ids: f.client_ids.includes(id) ? f.client_ids.filter((x) => x !== id) : [...f.client_ids, id],
  }));

  const doPreview = async () => {
    if (!form.name.trim()) return toast.error("Preencha o nome da campanha.");
    if (!form.template_name && !form.message.trim()) return toast.error("Escreva a mensagem ou escolha um template.");
    try {
      const { data } = await api.post("/campaigns/preview", form);
      setPreview(data); setStep(3);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const confirm = async () => {
    try {
      const { data } = await api.post("/campaigns", form);
      toast.success(form.scheduled_at ? "Campanha agendada!" : "Disparo iniciado!");
      onCreated(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const insertVar = (v) => set("message", (form.message || "") + v);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="campaign-wizard">
        <DialogHeader><DialogTitle className="font-display text-xl">Novo Disparo — Passo {step} de 3</DialogTitle></DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <div><Label>Nome da campanha</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex: Lembrete para clientes" className="mt-1.5" data-testid="campaign-name-input" /></div>
            <div>
              <Label>Destinatários</Label>
              <Select value={form.audience_type} onValueChange={(v) => set("audience_type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="campaign-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  <SelectItem value="selected">Escolher contatos{form.client_ids.length ? ` (${form.client_ids.length})` : ""}</SelectItem>
                  <SelectItem value="tag">Por tag</SelectItem>
                  <SelectItem value="status">Por status</SelectItem>
                  <SelectItem value="recent">Importados recentemente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.audience_type === "selected" && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="campaign-contact-picker">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Selecionar contatos</Label>
                  <span className="text-xs text-emerald-700 font-medium">{form.client_ids.length} selecionado(s)</span>
                </div>
                <Input value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} placeholder="Buscar por nome, WhatsApp ou e-mail..." className="h-9" data-testid="contact-picker-search" />
                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded">
                  {clientResults.length === 0 && <p className="text-xs text-slate-400 p-3 text-center">Digite para buscar ou nenhum contato encontrado.</p>}
                  {clientResults.map((cl) => {
                    const active = form.client_ids.includes(cl.id);
                    return (
                      <button type="button" key={cl.id} onClick={() => toggleClient(cl.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${active ? "bg-emerald-50" : ""}`}
                        data-testid={`contact-option-${cl.id}`}>
                        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${active ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
                          {active && <CheckCircle2 className="h-3 w-3 text-white" />}
                        </span>
                        <span className="flex-1 truncate">{cl.name}</span>
                        <span className="text-xs text-slate-400">{cl.whatsapp}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-400">Mostrando até 50 resultados — use a busca para refinar. Marque os contatos que receberão o disparo.</p>
              </div>
            )}
            {form.audience_type === "tag" && (
              <div><Label>Tag</Label>
                <Select value={form.audience_value || ""} onValueChange={(v) => set("audience_value", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="campaign-tag-select"><SelectValue placeholder="Selecione uma tag" /></SelectTrigger>
                  <SelectContent>{tags.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} ({t.count})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.audience_type === "status" && (
              <div><Label>Status</Label>
                <Select value={form.audience_value || ""} onValueChange={(v) => set("audience_value", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent><SelectItem value="ATIVO">Ativo</SelectItem><SelectItem value="INATIVO">Inativo</SelectItem><SelectItem value="PROSPECTO">Prospecto</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Intervalo mín. (s)</Label><Input type="number" min={0} value={form.interval_min} onChange={(e) => set("interval_min", parseInt(e.target.value) || 0)} className="mt-1.5" /></div>
              <div><Label>Intervalo máx. (s)</Label><Input type="number" min={0} value={form.interval_max} onChange={(e) => set("interval_max", parseInt(e.target.value) || 0)} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Máx. mensagens</Label><Input type="number" min={1} value={form.max_messages || ""} onChange={(e) => set("max_messages", e.target.value ? parseInt(e.target.value) : null)} placeholder="Sem limite" className="mt-1.5" /></div>
              <div>
                <Label className="flex items-center gap-2">Agendar envio</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="checkbox" checked={scheduling} onChange={(e) => { setScheduling(e.target.checked); if (!e.target.checked) set("scheduled_at", null); }} data-testid="campaign-schedule-toggle" />
                  <Input type="datetime-local" disabled={!scheduling} value={form.scheduled_at || ""} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="campaign-schedule-input" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={() => setStep(2)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="wizard-next-1">Próximo: Mensagem</Button></div>
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-2">
            <div className="space-y-3">
              <Label>Mensagem</Label>
              {metaTemplates.length > 0 && (
                <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 space-y-2" data-testid="campaign-meta-template-box">
                  <p className="text-xs font-semibold text-violet-800">Enviar por template aprovado (Meta) — chega na base fria</p>
                  <p className="text-xs text-slate-500">Para clientes que não te escreveram nas últimas 24h, é obrigatório um template aprovado. A imagem do template é enviada automaticamente.</p>
                  <select value={form.template_name || ""} onChange={(e) => selectMetaTpl(e.target.value)} className="w-full h-9 rounded-md border border-violet-200 px-2 text-sm bg-white" data-testid="campaign-meta-template-select">
                    <option value="">Não usar template (texto normal, só janela 24h)</option>
                    {metaTemplates.map((t) => <option key={t.name + t.language} value={t.name}>{t.name} ({t.language})</option>)}
                  </select>
                  {form.template_name && <p className="text-[11px] text-violet-700">Usando o template <strong>{form.template_name}</strong>. O texto abaixo é apenas a prévia do corpo aprovado.</p>}
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {templates.map((t) => <button key={t.id} onClick={() => set("message", t.content)} className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700">{t.name}</button>)}
              </div>
              <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={8} placeholder="Olá {nome}! ..." data-testid="campaign-message-input" />
              <div className="flex gap-2">{VARS.map((v) => <button key={v} onClick={() => insertVar(v)} className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 font-mono border border-emerald-100">{v}</button>)}</div>

              <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 p-3 space-y-2" data-testid="campaign-random-templates">
                <p className="text-xs font-semibold text-emerald-800">Envio aleatório de Modelos (QR Code)</p>
                <p className="text-xs text-slate-500">Escolha de quais Modelos o sistema vai sortear uma mensagem diferente para cada contato. Se não marcar nenhum, ele usa <strong>todos</strong> os Modelos.</p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {templates.map((t) => {
                    const active = form.selected_template_ids.includes(t.id);
                    return (
                      <button key={t.id} type="button" onClick={() => toggleTpl(t.id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}
                        data-testid={`campaign-tpl-toggle-${t.id}`}>
                        {active ? "✓ " : ""}{t.name}
                      </button>
                    );
                  })}
                  {templates.length === 0 && <span className="text-xs text-slate-400">Nenhum Modelo cadastrado ainda.</span>}
                </div>
                {form.selected_template_ids.length > 0 && <p className="text-[11px] text-emerald-700">{form.selected_template_ids.length} Modelo(s) selecionado(s) para sorteio</p>}
              </div>

              <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(1)}>Voltar</Button><Button onClick={doPreview} className="bg-emerald-500 hover:bg-emerald-600" data-testid="wizard-preview">Pré-visualizar</Button></div>
            </div>
            <PhonePreview text={form.message} />
          </div>
        )}

        {step === 3 && preview && (
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PhonePreview text={preview.sample || form.message} name={preview.sample_name} />
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <p className="text-sm text-slate-600 flex items-center gap-2"><Users className="h-4 w-4 text-emerald-600" /> Destinatários: <span className="font-bold text-slate-900">{preview.count}</span></p>
                  <p className="text-sm text-slate-600 flex items-center gap-2 mt-2"><Send className="h-4 w-4 text-emerald-600" /> Mensagem: <span className="font-bold text-slate-900">1</span></p>
                  <p className="text-xs text-slate-500 mt-3">Intervalo: {form.interval_min}s – {form.interval_max}s entre envios.</p>
                  {form.scheduled_at && <p className="text-xs text-indigo-600 mt-2 flex items-center gap-1"><Clock className="h-3 w-3" /> Agendado: {new Date(form.scheduled_at).toLocaleString("pt-BR")}</p>}
                </div>
                {preview.count === 0 && <p className="text-sm text-red-600">Nenhum destinatário válido com WhatsApp. Ajuste os filtros.</p>}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(2)}>Voltar</Button>
                  <Button onClick={confirm} disabled={preview.count === 0} className="bg-emerald-500 hover:bg-emerald-600 flex-1" data-testid="campaign-confirm-button">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> {form.scheduled_at ? "Agendar Disparo" : "Confirmar Disparo"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PhonePreview({ text, name }) {
  const rendered = (text || "Sua mensagem aparecerá aqui...").replace(/\{nome\}/g, name || "João").replace(/\{whatsapp\}/g, "+5583999999999").replace(/\{email\}/g, "joao@email.com");
  return (
    <div className="flex flex-col items-center">
      <div className="w-full max-w-[280px] rounded-[2rem] bg-slate-900 p-2.5 shadow-xl">
        <div className="rounded-[1.5rem] overflow-hidden" style={{ background: "#ECE5DD" }}>
          <div className="bg-emerald-700 px-4 py-3 flex items-center gap-2"><Smartphone className="h-4 w-4 text-white" /><span className="text-white text-sm font-medium">Clínica</span></div>
          <div className="p-3 min-h-[220px]">
            <div className="bg-emerald-100 rounded-lg rounded-tl-none p-3 shadow-sm max-w-[90%]">
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{rendered}</p>
              <p className="text-[10px] text-slate-400 text-right mt-1">agora ✓✓</p>
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">Pré-visualização WhatsApp</p>
    </div>
  );
}

function MonitorDialog({ id, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/campaigns/${id}`).then((r) => setC(r.data));
    load();
    const t = setInterval(load, 1200);
    return () => clearInterval(t);
  }, [id]);

  if (!c) return null;
  const pct = c.total ? Math.round(((c.sent + c.errors) / c.total) * 100) : 0;
  const control = async (action) => { await api.post(`/campaigns/${id}/control`, { action }); toast.success("Ação aplicada."); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="campaign-monitor">
        <DialogHeader><DialogTitle className="font-display text-xl">{c.name}</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">
              {c.status === "andamento" ? "ENVIANDO" : c.status === "aguardando" ? "PAUSA ENTRE ENVIOS" : c.status === "paused" ? "PAUSADO" : c.status === "concluida" ? "CONCLUÍDO" : c.status === "cancelled" ? "CANCELADO" : c.status === "agendada" ? "AGENDADO" : c.status}
            </span>
            {c.demo_mode && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">MODO DEMONSTRAÇÃO</span>}
          </div>
          <div>
            <Progress value={pct} className="h-3" />
            <p className="text-right text-xs text-slate-500 mt-1">{pct}%</p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100"><p className="text-xl font-bold text-emerald-600">{c.sent}</p><p className="text-xs text-slate-500">Enviadas</p></div>
            <div className="p-3 rounded-lg bg-sky-50 border border-sky-100"><p className="text-xl font-bold text-sky-600">{c.pending ?? 0}</p><p className="text-xs text-slate-500">Pendentes</p></div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-100"><p className="text-xl font-bold text-red-500">{c.errors}</p><p className="text-xs text-slate-500">Erros</p></div>
          </div>
          {c.current_contact && ["andamento"].includes(c.status) && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
              <p className="text-xs text-slate-400">Contato atual</p>
              <p className="text-sm font-medium text-slate-800">{c.current_contact.name}</p>
              <p className="text-xs font-mono text-slate-500">{c.current_contact.whatsapp}</p>
            </div>
          )}
          <div className="flex gap-2">
            {c.status === "andamento" && <Button variant="outline" onClick={() => control("pause")} className="flex-1" data-testid="monitor-pause"><Pause className="h-4 w-4 mr-1" /> Pausar</Button>}
            {c.status === "paused" && <Button onClick={() => control("resume")} className="flex-1 bg-emerald-500 hover:bg-emerald-600" data-testid="monitor-resume"><Play className="h-4 w-4 mr-1" /> Retomar</Button>}
            {c.status === "agendada" && <Button onClick={() => control("start")} className="flex-1 bg-emerald-500 hover:bg-emerald-600"><Play className="h-4 w-4 mr-1" /> Iniciar agora</Button>}
            {["andamento", "paused", "agendada"].includes(c.status) && <Button variant="outline" onClick={() => control("cancel")} className="flex-1 text-red-600 border-red-200" data-testid="monitor-cancel"><X className="h-4 w-4 mr-1" /> Cancelar</Button>}
            <Button variant="ghost" onClick={onClose}>Fechar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
